import { Button } from "@/components/ui";
import type { AddToCalendarProps } from "@/store/booking/useBookingState";
import { showToast } from "@/components/ui/toast/show-toast";
import { buildDescriptionForSingleVisit, getVisitDateTime } from "./lib";

const formatDateForICS = (dateTime: string): string => {
  const date = new Date(dateTime);
  return date.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
};

const escapeICS = (text: string): string => {
  return text.replace(/[,;\\]/g, "\\$&").replace(/\n/g, "\\n");
};

const foldLine = (line: string, maxLength = 75): string[] => {
  if (line.length <= maxLength) {
    return [line];
  }
  const lines: string[] = [];
  let current = line;
  while (current.length > maxLength) {
    lines.push(current.substring(0, maxLength));
    current = " " + current.substring(maxLength);
  }
  if (current.length > 0) {
    lines.push(current);
  }
  return lines;
};

const generateICS = (
  title: string,
  location: string,
  visits: Array<{
    startDateTime: string;
    endDateTime: string;
    description: string;
  }>,
): string => {
  const now = formatDateForICS(new Date().toISOString());
  const lines: string[] = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Luce//Booking Calendar//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
  ];

  for (const visit of visits) {
    const startDate = formatDateForICS(visit.startDateTime);
    const endDate = formatDateForICS(visit.endDateTime);
    const uid = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}@luce.com`;

    lines.push("BEGIN:VEVENT");
    lines.push(`UID:${uid}`);
    lines.push(`DTSTAMP:${now}`);
    lines.push(`DTSTART:${startDate}`);
    lines.push(`DTEND:${endDate}`);

    const summary = `SUMMARY:${escapeICS(title)}`;
    lines.push(...foldLine(summary));

    const description = `DESCRIPTION:${escapeICS(visit.description)}`;
    lines.push(...foldLine(description));

    const locationLine = `LOCATION:${escapeICS(location)}`;
    lines.push(...foldLine(locationLine));

    lines.push("STATUS:CONFIRMED");
    lines.push("SEQUENCE:0");
    lines.push("END:VEVENT");
  }

  lines.push("END:VCALENDAR");

  return lines.join("\r\n");
};

const downloadICS = (icsContent: string, filename: string) => {
  const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

export const AddToCalendarButton = ({
  title,
  description,
  location,
  visits,
}: AddToCalendarProps) => {
  const handleDownload = () => {
    try {
      const events: Array<{
        startDateTime: string;
        endDateTime: string;
        description: string;
      }> = [];

      for (const visit of visits) {
        const { startDateTime, endDateTime } = getVisitDateTime(visit);
        const visitDescription = buildDescriptionForSingleVisit(
          description,
          visit,
        );
        events.push({
          startDateTime,
          endDateTime,
          description: visitDescription,
        });
      }

      const icsContent = generateICS(title, location, events);
      const filename = `${title} Appointment.ics`;
      downloadICS(icsContent, filename);

      showToast({
        type: "success",
        title: "Your calendar has been downloaded",
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      showToast({
        type: "error",
        title: `Error downloading calendar: ${errorMessage}`,
      });
    }
  };

  return (
    <Button
      variant="secondary"
      color="CTA2"
      fullWidth="full"
      size="md"
      onClick={handleDownload}
      children="Add to Calendar"
    />
  );
};

export const AddToCalendar = AddToCalendarButton;
